/* global GOOGLE_API_KEY:true */
import React, { useMemo, useCallback, useEffect } from "react";
import { EnvironmentOutlined } from "@ant-design/icons";
import { PageHeaderWrapper, PageLoading } from "@ant-design/pro-layout";
import { Descriptions, Card, Tag, Steps, Typography, Row, Col, Divider } from "antd";
import { connect } from "dva";
import GoogleMapReact from "google-map-react";
import { TrailerType, TrailerTypeMap } from "@/utils/enum";
import { formatPhoneNumber, getGuidFirst8, getLoadStatusTranslation, getUrlParams, DEFAULT_ID } from "@/utils/utils";
import DealerExchangeAlert from "@/components/DealerExchangeAlert";
import ETAWindow from "@/components/ETAWindow";
import LoadActionsButton from "@/components/LoadActionsButton";
import LiveMarker from "@/components/MapMarkers/LiveMarker/LiveMarker";
import VehiclesList from "@/components/VehiclesList";
import NotFound from "@/pages/404";

const { Step } = Steps;
const { Title } = Typography;

const MapMarker = ({ status }) => <LiveMarker status={status} />;

// Inline styles
const cardHead = { color: "white", backgroundColor: "#22316C" };
const grid = { md: { span: 12 }, sm: { span: 24 } };

function midPintCalculation(pickupCoord, dropoffCoord) {
  return (pickupCoord + dropoffCoord) / 2;
}

const LoadView = ({ loading, transporterLoadsView, configPerm, dispatch }) => {
  const loadId = useMemo(() => getUrlParams("loadId"), []);
  const data = useMemo(() => transporterLoadsView.data, [transporterLoadsView]);

  const extraActionButtons = useMemo(() => {
    if (!data) {
      return null;
    }

    const { id } = data;

    return (
      <LoadActionsButton
        loadId={data.id}
        loadData={data}
        refreshPageData={() => {
          dispatch({
            type: "transporterLoadsView/updateSingleTransporterLoad",
            loadId: id,
          });
        }}
      />
    );
  }, [data, dispatch]);

  const extraMapRenders = useCallback(
    (map, maps) => {
      let directionsService = new maps.DirectionsService();
      let directionsRender = new maps.DirectionsRenderer();
      directionsRender.setMap(map);
      directionsService.route(
        {
          origin: `${data.shipment.pickupAddress.latitude},${data.shipment.pickupAddress.longitude}`,
          destination: `${data.shipment.dropOffAddress.latitude},${data.shipment.dropOffAddress.longitude}`,
          travelMode: "DRIVING",
        },
        (response, status) => {
          if (status === "OK") {
            directionsRender.setDirections(response);
          } else {
            window.alert("Directions request failed due to " + status);
          }
        },
      );
    },
    [data],
  );

  useEffect(() => {
    dispatch({
      type: "transporterLoadsView/fetchSingleTransporterLoad",
      loadId,
    });
  }, [loadId, dispatch]);

  if (typeof loading === "undefined" || loading === true) {
    return <PageLoading />;
  }

  if (data === null || data === undefined) {
    return <NotFound />;
  }

  return (
    <PageHeaderWrapper
      onBack={() => window.history.back()}
      title={`Transporter Load: ${getGuidFirst8(data?.id)}`}
      extra={extraActionButtons}
      tags={[
        <Tag key="load-status" color="blue">
          {getLoadStatusTranslation(data.status, data.signedAtPickup, data.signedAtDropoff)}
        </Tag>,
        <Tag key="trailer-type" color={data?.shipment?.trailerType === TrailerType.Enclosed ? "purple" : "blue"}>
          {TrailerTypeMap[data?.shipment?.trailerType]}
        </Tag>,
      ]}
    >
      <Card bordered={false}>
        {data.childLoadId !== DEFAULT_ID && data.childLoadId && (
          <DealerExchangeAlert
            relativeUrl={"/loads/all/view"}
            loadId={data.childLoadId}
            exchangeType={data.shipment?.dealerExchangeType}
            style={{ marginLeft: 20, marginBottom: 16 }}
            assignedEntityId={data.entityId}
            isRequired={data.isExchangeRequired}
            exchangeData={{
              childLoadId: data.childLoadId,
              childLoadStatus: data.childLoadStatus,
              childLoadAssignedEntity: data.childLoadAssignedEntity,
              parentLoadId: data.parentLoadId,
              parentLoadStatus: data.parentLoadStatus,
              parentLoadAssignedEntity: data.parentLoadAssignedEntity,
              childShipmentStatus: data.childShipmentStatus,
              parentShipmentStatus: data.parentShipmentStatus,
            }}
          />
        )}
        {data.parentLoadId !== DEFAULT_ID && data.parentLoadId && (
          <DealerExchangeAlert
            isChild
            relativeUrl={"/loads/all/view"}
            loadId={data.parentLoadId}
            exchangeType={data.shipment?.dealerExchangeType}
            isRequired={data.isExchangeRequired}
            style={{ marginLeft: 20, marginBottom: 16 }}
            assignedEntityId={data.entityId}
            exchangeData={{
              childLoadId: data.childLoadId,
              childLoadStatus: data.childLoadStatus,
              childLoadAssignedEntity: data.childLoadAssignedEntity,
              parentLoadId: data.parentLoadId,
              parentLoadStatus: data.parentLoadStatus,
              parentLoadAssignedEntity: data.parentLoadAssignedEntity,
            }}
          />
        )}
        {/* Dealer Details */}
        <Row gutter={[16, 16]} justify="space-between">
          <Col {...grid}>
            <Card type="inner" title="Shipper" style={{ height: "100%" }} headStyle={cardHead}>
              <Descriptions title={data.shipment.dealer.name} column={1}>
                <Descriptions.Item key="address" style={{ paddingBottom: "0" }}>
                  <div>{data.shipment.dealer.completedAddress}</div>
                </Descriptions.Item>
                <Descriptions.Item key="phone" label="Phone" style={{ paddingBottom: "0" }}>
                  {formatPhoneNumber(data.shipment.dealer.phoneNumber, data.shipment.dealer.phoneExt)}
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
          <Col {...grid}>
            <Card type="inner" title="Shipper Contact" style={{ height: "100%" }} headStyle={cardHead}>
              <Descriptions
                title={data?.contactShipperFullName ? data?.contactShipperFullName : "N/A"}
                column={1}
                size="small"
              >
                {/* <Descriptions.Item key="email" style={{ paddingBottom: "0" }}>
                  {data.dealer.email}
                </Descriptions.Item>
                <Descriptions.Item key="phone" style={{ paddingBottom: "0" }}>
                  {formatPhoneNumber(
                    data.dealer.phoneNumber1,
                    data.dealer.phoneExt,
                  )}
                </Descriptions.Item> */}
              </Descriptions>
            </Card>
          </Col>
        </Row>
        <Row gutter={[16, 16]} justify="space-between">
          <Col {...grid}>
            <Card type="inner" title="Pickup Location" style={{ height: "100%" }} headStyle={cardHead}>
              <Descriptions title={data.shipment.pickupAddress.name} column={1}>
                <Descriptions.Item key="pickupAddress" style={{ paddingBottom: "1rem" }}>
                  <div>{data.shipment.pickupAddress.completedAddress}</div>
                </Descriptions.Item>
                <Descriptions.Item key="pickupName" label="Contact" style={{ paddingBottom: "0" }}>
                  {data.shipment.pickupAddress.contactName}
                </Descriptions.Item>
                <Descriptions.Item key="pickupPhone" label="Phone" style={{ paddingBottom: "0" }}>
                  {formatPhoneNumber(
                    data.shipment.pickupAddress.contactPhone,
                    data.shipment.pickupAddress.contactPhoneExtension,
                  )}
                </Descriptions.Item>
                <Descriptions.Item key="pickupEta" label="Pickup ETA" style={{ paddingBottom: "0" }}>
                  <ETAWindow etaDate={data.pickupETA} etaTimeSlotIndex={data.pickupETATimeSlot} />
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
          <Col {...grid}>
            <Card type="inner" title="Drop off Location" style={{ height: "100%" }} headStyle={cardHead}>
              <Descriptions title={data.shipment.dropOffAddress.name} column={1}>
                <Descriptions.Item key="dropoffAddress" style={{ paddingBottom: "1rem" }}>
                  <div>{data.shipment.dropOffAddress.completedAddress}</div>
                </Descriptions.Item>
                <Descriptions.Item key="dropoffName" label="Contact" style={{ paddingBottom: "0" }}>
                  {data.shipment.dropOffAddress.contactName}
                </Descriptions.Item>
                <Descriptions.Item key="dropoffPhone" label="Phone" style={{ paddingBottom: "0" }}>
                  {formatPhoneNumber(
                    data.shipment.dropOffAddress.contactPhone,
                    data.shipment.dropOffAddress.contactPhoneExtension,
                  )}
                </Descriptions.Item>
                <Descriptions.Item key="dropoffEta" label="Drop off ETA" style={{ paddingBottom: "0" }}>
                  <ETAWindow etaDate={data.dropOffETA} etaTimeSlotIndex={data.dropOffETATimeSlot} />
                </Descriptions.Item>
              </Descriptions>
            </Card>
          </Col>
        </Row>
        <Row gutter={[16, 16]} justify="space-between">
          <Col {...grid}>
            <Card type="inner" title="Pickup Notes" style={{ height: "100%" }} headStyle={cardHead}>
              <Descriptions column={1}>
                {data.shipment.pickupAddress.notes && (
                  <Descriptions.Item label="Instructions" key="pickupInstructions" style={{ paddingBottom: 16 }}>
                    {data.shipment.pickupAddress.notes}
                  </Descriptions.Item>
                )}

                {data.notesAtPickup && (
                  <Descriptions.Item label="Driver Notes" key="pickupNotes" style={{ paddingBottom: 0 }}>
                    {data.notesAtPickup}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>
          </Col>
          <Col {...grid}>
            <Card type="inner" title="Drop-off Notes" style={{ height: "100%" }} headStyle={cardHead}>
              <Descriptions column={1}>
                {data.shipment.dropOffAddress.notes && (
                  <Descriptions.Item label="Instructions" key="dropoffInstructions" style={{ paddingBottom: 16 }}>
                    {data.shipment.dropOffAddress.notes}
                  </Descriptions.Item>
                )}

                {data.notesAtDropoff && data.notesAtDropoff !== "Created via Status dropdown" && (
                  <Descriptions.Item label="Driver Notes" key="dropoffNotes" style={{ paddingBottom: 0 }}>
                    {data.notesAtDropoff}
                  </Descriptions.Item>
                )}

                {data.notesAtDropoff &&
                  data.notesAtDropoff === "Created via Status dropdown" &&
                  configPerm["NoteService.Notes"] && (
                    <Descriptions.Item label="Driver Notes" key="dropoffNotes" style={{ paddingBottom: 0 }}>
                      {"Delivery was completed by Autosled admin"}
                    </Descriptions.Item>
                  )}
              </Descriptions>
            </Card>
          </Col>
        </Row>
        <Divider
          style={{
            marginBottom: 32,
          }}
        />
        <VehiclesList
          pricesSpacing={1}
          vehicles={transporterLoadsView.data.shipment.vehicles || []}
          priceExtractor={item => item.quote.transporterPrice}
          shipmentData={transporterLoadsView.data.shipment}
        />

        <Title level={4}>
          Distance: {data.shipment.distance} Miles ({data.shipment.transporterPricePerMile} per mile)
        </Title>
        <Steps
          style={{
            marginBottom: 32,
          }}
        >
          <Step
            status="finish"
            title="Pickup"
            description={data.shipment.pickupAddress.completedAddress}
            icon={<EnvironmentOutlined style={{ color: "#4888F4" }} />}
          />
          <Step
            status="finish"
            title="DropOff"
            description={data.shipment.dropOffAddress.completedAddress}
            icon={<EnvironmentOutlined style={{ color: "#DD4B3E" }} />}
          />
        </Steps>
        <div style={{ height: 500 }}>
          <GoogleMapReact
            bootstrapURLKeys={{ key: GOOGLE_API_KEY }}
            defaultCenter={{
              lat: midPintCalculation(data.shipment.pickupAddress.latitude, data.shipment.dropOffAddress.latitude),
              lng: midPintCalculation(data.shipment.pickupAddress.longitude, data.shipment.dropOffAddress.longitude),
            }}
            defaultZoom={10}
            yesIWantToUseGoogleMapApiInternals
            onGoogleApiLoaded={({ map, maps }) => extraMapRenders(map, maps)}
          >
            <MapMarker
              lat={data.shipment.pickupAddress.latitude}
              lng={data.shipment.pickupAddress.longitude}
              status={data.shipment.status}
            />
          </GoogleMapReact>
        </div>
      </Card>
    </PageHeaderWrapper>
  );
};

export default connect(({ transporterLoadsView, loading, configs }) => ({
  transporterLoadsView,
  loading: loading.effects["transporterLoadsView/fetchSingleTransporterLoad"],
  configPerm: configs.configPerm,
}))(LoadView);
